import { Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { TYPE_LABEL, fmtDate } from "@/lib/api";

export const AccessBadge = ({ access }) =>
  access === "free" ? null : (
    <span data-testid="access-badge" className={`inline-flex items-center gap-1 overline !text-[9px] px-2 py-1 ${access === "institutional" ? "bg-[#0A0A0A] text-white" : "bg-[#FFF1F0] text-[#B8241C] border border-[#F7CFCB]"}`}>
      <Lock size={10} /> {access === "institutional" ? "Institutional" : "Pro"}
    </span>
  );

export const SponsorBadge = ({ name, label = "Sponsored by" }) =>
  name ? (
    <span data-testid="sponsor-badge" className="inline-flex items-center gap-1 text-[10px] font-mono-d uppercase tracking-wider px-2 py-1 bg-[#F6F6F6] border border-[#DADADA] text-[#E5332A]">
      {label} {name}
    </span>
  ) : null;

export const ArticleMeta = ({ a, light }) => (
  <div className={`flex flex-wrap items-center gap-2 overline ${light ? "text-[#E7E7E7]/80" : "text-[#E5332A]"}`}>
    <span>{TYPE_LABEL[a.type] || a.type}</span><span>·</span><span>{a.category}</span>
  </div>
);

export const ArticleCard = ({ a, variant = "default", index = 0 }) => {
  const delay = { animationDelay: `${index * 70}ms` };
  if (variant === "compact") {
    return (
      <Link to={`/article/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block py-5 border-b border-[#E7E7E7] fade-up" style={delay}>
        <ArticleMeta a={a} />
        <h3 className="font-display text-lg font-semibold leading-snug mt-2 group-hover:text-[#E5332A] transition-colors">{a.title}</h3>
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-[#575757]">
          <span className="whitespace-nowrap">{fmtDate(a.published_at)} · {a.read_time} min</span>
          <AccessBadge access={a.access} />
          {a.sponsor_name && <SponsorBadge name={a.sponsor_name} label="Partner:" />}
        </div>
      </Link>
    );
  }
  return (
    <Link to={`/article/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block fade-up" style={delay}>
      <div className="img-zoom overflow-hidden aspect-[16/9] bg-[#E7E7E7]">
        <img src={a.image} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="pt-5">
        <div className="flex items-center justify-between gap-2"><ArticleMeta a={a} /><AccessBadge access={a.access} /></div>
        <h3 className="font-display text-xl font-semibold leading-snug mt-3 group-hover:text-[#E5332A] transition-colors">{a.title}</h3>
        <p className="text-sm text-[#575757] mt-3 leading-relaxed line-clamp-3">{a.excerpt}</p>
        <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-[#575757]">
          <span>{a.author}</span><span>· {a.read_time} min read</span>
          {a.takeaways?.length > 0 && <span className="flex items-center gap-1 text-[#E5332A]"><Sparkles size={12} /> AI takeaways</span>}
        </div>
        {a.sponsor_name && <div className="mt-3"><SponsorBadge name={a.sponsor_name} /></div>}
      </div>
    </Link>
  );
};
